import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { uncoveryApi } from "../../services";

const initialState = {
  stakeholders: [],
  selectedStakeholder: null,
  fetchStatus: "idle",
  addStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  error: null,
};

const options = {
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

export const addOneStakeholder = createAsyncThunk(
  "stakeholders/addOne",
  async (body, { rejectWithValue }) => {
    try {
      const response = await uncoveryApi.post("/stakeholders", body, options);
      if (response.status === 201) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.request);
      }
    } catch (err) {
      return rejectWithValue(err.response?.data.request);
    }
  }
);

export const updateStakeholder = createAsyncThunk(
  "stakeholders/update",
  async ({ stakeholderId, body }, { rejectWithValue }) => {
    try {
      const response = await uncoveryApi.patch(
        `/stakeholders/id/${stakeholderId}`,
        body,
        options
      );
      if (response.status === 200) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.request);
      }
    } catch (err) {
      return rejectWithValue(err.response?.data.request);
    }
  }
);

export const deleteStakeholder = createAsyncThunk(
  "stakeholders/delete",
  async (stakeholderId, { rejectWithValue }) => {
    try {
      const response = await uncoveryApi.delete(
        `/stakeholders/id/${stakeholderId}`,
        options
      );
      if (response.status === 200) {
        return stakeholderId;
      } else {
        return rejectWithValue(response.data.request);
      }
    } catch (err) {
      return rejectWithValue(err.response?.data.request);
    }
  }
);

export const fetchStakeholders = createAsyncThunk(
  "stakeholders/fetchOne",
  async ({ entityId }, { rejectWithValue }) => {
    try {
      const response = await uncoveryApi.get(
        `/stakeholders/entity/${entityId}`
      );
      if (response.status === 200) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.request);
      }
    } catch (e) {
      return rejectWithValue(e.response?.data.request);
    }
  }
);

const stakeholdersSlice = createSlice({
  name: "stakeholders",
  initialState,
  reducers: {
    setSelectedStakeholder: (state, { payload }) => {
      state.selectedStakeholder = payload;
    },
    resetSelectedStakeholder: (state) => {
      state.selectedStakeholder = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: {
    [fetchStakeholders.pending]: (state) => {
      state.fetchStatus = "loading";
    },
    [fetchStakeholders.fulfilled]: (state, { payload }) => {
      state.stakeholders = payload;
      state.fetchStatus = "success";
    },
    [fetchStakeholders.rejected]: (state, { payload }) => {
      state.error = payload?.message || "Failed to fetch stakeholders";
      state.fetchStatus = "failed";
    },
    [addOneStakeholder.pending]: (state) => {
      state.addStatus = "loading";
    },
    [addOneStakeholder.fulfilled]: (state, { payload }) => {
      state.stakeholders.push(payload);
      state.addStatus = "success";
    },
    [addOneStakeholder.rejected]: (state, { payload }) => {
      state.error = payload?.message || "Failed to add stakeholder";
      state.addStatus = "failed";
    },
    [updateStakeholder.pending]: (state) => {
      state.updateStatus = "loading";
    },
    [updateStakeholder.fulfilled]: (state, { payload }) => {
      state.stakeholders = state.stakeholders.map((stakeholder) =>
        stakeholder.id === payload.id ? payload : stakeholder
      );
      state.updateStatus = "success";
    },
    [updateStakeholder.rejected]: (state, { payload }) => {
      state.error = payload?.message || "Failed to update stakeholder";
      state.updateStatus = "failed";
    },
    [deleteStakeholder.pending]: (state) => {
      state.deleteStatus = "loading";
    },
    [deleteStakeholder.fulfilled]: (state, { payload }) => {
      state.stakeholders = state.stakeholders.filter(
        (stakeholder) => stakeholder.id !== payload
      );
      state.deleteStatus = "success";
    },
    [deleteStakeholder.rejected]: (state, { payload }) => {
      state.error = payload?.message || "Failed to delete stakeholder";
      state.deleteStatus = "failed";
    },
  },
});

export const { setSelectedStakeholder, resetSelectedStakeholder, clearError } =
  stakeholdersSlice.actions;

export default stakeholdersSlice.reducer;
